import { useCallback, useEffect, useState } from "react";
import {
  classificationsToDropdownOptions,
  getFallbackClassifications,
  loadClassificationsForConsumers,
} from "../helpers/catalogs/classificationCatalog";
import {
  getFallbackPayrollTypes,
  loadPayrollTypesForConsumers,
  payrollTypesToDropdownOptions,
} from "../helpers/catalogs/payrollTypeCatalog";
import { usePermissions } from "./usePermissions";

/**
 * Loads organization-owned catalog dropdown options.
 * Actor org is required for live reads; hardcoded fallbacks are offline/error only.
 */
export function useConfigurationCatalogs() {
  const { actor } = usePermissions();
  const [classificationOptions, setClassificationOptions] = useState(() =>
    classificationsToDropdownOptions(getFallbackClassifications()),
  );
  const [payrollTypeOptions, setPayrollTypeOptions] = useState(() =>
    payrollTypesToDropdownOptions(getFallbackPayrollTypes()),
  );
  const [loading, setLoading] = useState(true);

  const refreshCatalogs = useCallback(async () => {
    const [classifications, payrollTypes] = await Promise.all([
      loadClassificationsForConsumers(undefined, actor),
      loadPayrollTypesForConsumers(undefined, actor),
    ]);

    setClassificationOptions(classificationsToDropdownOptions(classifications));
    setPayrollTypeOptions(payrollTypesToDropdownOptions(payrollTypes));
  }, [actor]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        if (!cancelled) {
          await refreshCatalogs();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [refreshCatalogs]);

  return {
    classificationOptions,
    payrollTypeOptions,
    loading,
    refreshCatalogs,
  };
}
